<script setup lang="ts">
import { computed, useTemplateRef } from "vue";

import { cardKey, cardMetaKey } from "@/components/StickyNote/injectKeys";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import ScreenReaderMessage from "@/components/ui/a11y/ScreenReaderMessage.vue";
import { useBoardStore } from "@/store/board";
import { useUserStore } from "@/store/user";
import { injectStrict } from "@/utils/vue";

import { useKeyboardDrag } from "./useKeyboardDrag";

const card = injectStrict(cardKey);
const cardMeta = injectStrict(cardMetaKey);

const moveButtonRef = useTemplateRef("moveButton");

const emit = defineEmits(["deactivate"]);

const { moveMode, screenReaderMessage, handleKeydown, startMove, stopMove } = useKeyboardDrag({
  card,
  cardMeta,
  deactivate: () => emit("deactivate"),
});

defineExpose({
  focusAndStartMove: () => {
    moveButtonRef.value?.focus();
    startMove();
  },
});

/**
 * Only show the move controls on boards where the user can drag the sticky
 * and hide it from observers
 */
const showControls = computed(() => {
  const onBoard = ["team", "program", "solution", "risk", "flex"].includes(
    useBoardStore().currentBoard().type,
  );

  return onBoard && useUserStore().isAllowed("edit");
});

/**
 * Toggle the move mode on and off
 */
const toggleMoveMode = () => {
  if (moveMode.value) {
    stopMove();
  } else {
    startMove();
  }
};
</script>

<template>
  <!-- The move control MUST be contained within an element with role="application", 
       otherwise screen readers (JAWS) will intercept the arrow keys - note that, 
       for VoiceOver, you still have to press Option+arrow -->
  <div v-if="showControls" class="sticky-note-accessibility-controls" role="application">
    <IconButton
      ref="moveButton"
      class="visible-on-focus"
      icon="arrow/move"
      :text="$t('label.moveSticky.controls')"
      :activated="moveMode"
      @click.stop="toggleMoveMode"
      @keydown="handleKeydown"
      @blur="stopMove()"
    />
    <ScreenReaderMessage>{{ screenReaderMessage }}</ScreenReaderMessage>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/z-index";

.sticky-note-accessibility-controls {
  position: absolute;
  bottom: 25px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;

  &:not(:focus-within) {
    opacity: 0;
    z-index: z-index.$a11y-hidden;
    bottom: unset;
    right: unset;
  }

  :deep(.visible-on-focus) {
    position: relative;
    color: inherit !important;
  }
}
</style>
